import React, { useState, useEffect, useContext } from 'react';
import { Link, useLocation } from 'react-router-dom';
import GlobalContext from '../contexts/global.js';
import StateContext from '../contexts/state.js';
import UserContext, { isPartner, isDirector } from '../contexts/user.js';

import '../styles/navigationSideBar.css';

const getIconUrl = (icon) => `${process.env.PUBLIC_URL}/assets/icons/categories/${icon}.svg`;

/* A "show everything" option clears the rest, others toggle and fall back to "show everything" once none is left */
const toggleOption = (options, selected = [], option) => {
    const everything = options.filter(({ ignoreFilter }) => ignoreFilter);
    if(option.ignoreFilter) {
        return everything;
    }

    const keys = selected.filter(({ ignoreFilter }) => !ignoreFilter).map(({ key }) => key);
    const nextKeys = keys.includes(option.key) ? keys.filter(key => key !== option.key) : [ ...keys, option.key ];

    return nextKeys.length ? options.filter(({ key }) => nextKeys.includes(key)) : everything;
};

const selectedLabel = (selected = []) => selected.map(({ value }) => value).join(", ");

const Item = ({
    label,
    icon,
    link,
    options,
    selected = [],
    onSelect
}) => {
    const { updateState } = useContext(StateContext);
    const location = useLocation();
    const [expanded, setExpanded] = useState(false);

    const Component = link ? Link : "div";
    const hasSubmenu = !link && options?.length > 0;
    const isCurrentPage = link && location.pathname === link;

    return (
        <Component
            className={
                `nav-item${ isCurrentPage ? " nav-item--current" : "" }${ hasSubmenu && expanded ? " nav-item--expanded" : "" }`
            }
            onClick={
                () => {
                    updateState({ currentCategories: [] });
                    hasSubmenu && setExpanded(wasExpanded => !wasExpanded);
                }
            }
            { ...(link && {
                to: link
            })}
        >
            <div className="nav-item-header">
                <div
                    className="icon"
                    style={{ maskImage: `url(${getIconUrl(icon)})` }}
                />
                <div className="label" title={label}>
                    {label}
                </div>
                { hasSubmenu && <div className="chevron" /> }
            </div>
            {
                hasSubmenu && (
                    <div className="submenu">
                        <div className="submenu-inner">
                            {
                                options.map(option => (
                                    <div
                                        className={
                                            `nav-subitem${ selected.some(({ key }) => key === option.key) ? " nav-subitem--current" : "" }`
                                        }
                                        key={ option.key }
                                        title={ option.value }
                                        onClick={
                                            event => {
                                                event.stopPropagation();
                                                onSelect?.(option);
                                            }
                                        }
                                    >
                                        <span className="nav-subitem-label">
                                            { option.value }
                                        </span>
                                    </div>
                                ))
                            }
                        </div>
                    </div>
                )
            }
        </Component>
    );
};

const NavigationSideBar = ({ onUpdate = () => {} }) => {
    const { globalContext } = useContext(GlobalContext);
    const { state, updateState } = useContext(StateContext);
    const { userContext } = useContext(UserContext);
    const location = useLocation();

    const [los, setLos] = useState([]);
    const [locations, setLocations] = useState([]);
    const [navExpanded, setNavExpanded] = useState(false);

    useEffect(() => {
        setLos(globalContext?.los || []);
    }, [globalContext?.los]);

    useEffect(() => {
        setLocations(globalContext?.locations || []);
    }, [globalContext?.locations]);

    useEffect(() => {
        onUpdate();
    }, [state.currentLos, state.currentLocation]);

    useEffect(() => {
        setNavExpanded(false);
    }, [location.pathname]);

    return (
        <div className="navigation-side-bar">
            <button
                type="button"
                className="nav-toggle"
                aria-expanded={navExpanded}
                onClick={
                    () => setNavExpanded(wasExpanded => !wasExpanded)
                }
            >
                <span className="label">Menu</span>
                <span className="chevron" />
            </button>

            <nav className={`crumbs${ navExpanded ? " crumbs--expanded" : "" }`}>
                <h2>My content</h2>
                <ol className="navigation-group">
                    <li>
                        <Item
                            label = "For you"
                            icon = "home"
                            link = "/"
                        />
                    </li>
                    <li>
                        <Item
                            label = "Firmwide news"
                            icon = "globe"
                            link = "/firmwide-news"
                        />
                    </li>
                    {
                        false && isPartner(userContext) && (
                            <li>
                                <Item
                                    label = "Partners"
                                    icon = "people"
                                    link = "/partners"
                                />
                            </li>
                        )
                    }
                    {
                        false && isDirector(userContext) && (
                            <li>
                                <Item
                                    label = "Directors"
                                    icon = "people"
                                    link = "/directors"
                                />
                            </li>
                        )
                    }
                </ol>

                <h2>Browse</h2>
                <ol className="navigation-group">
                    <li>
                        <Item
                            label = "Local stories"
                            icon = "poi"
                            link = "/local-stories"
                        />
                    </li>
                    <li>
                        <Item
                            label = {state.currentLos?.length ? `LoS: ${ selectedLabel(state.currentLos) }` : "LoS"}
                            icon = "grid"
                            options = {los}
                            selected = {state.currentLos}
                            onSelect = {
                                selectedOption => updateState({
                                    currentLos: toggleOption(los, state.currentLos, selectedOption)
                                })
                            }
                        />
                    </li>
                    { false && (
                        <li>
                            <Item
                                label = {`Location: ${ selectedLabel(state.currentLocation) || "All" }`}
                                icon = "poi"
                                options = {locations}
                                selected = {state.currentLocation}
                                onSelect = {
                                    selectedOption => updateState({
                                        currentLocation: toggleOption(locations, state.currentLocation, selectedOption)
                                    })
                                }
                            />
                        </li>
                    )}
                </ol>
            </nav>
        </div>
    );
};

export default NavigationSideBar;
